import { registerWeapon } from '../../weapons';
import { createProjectile } from '../../weapons/registry';
import { SMALL_CRATER } from '../../terrain/crater-shapes';
import { KOMA_SPIN, KOMA_RIBBON, updateKomaRibbon } from './constants';
import type { RibbonPoint } from './constants';
import type { WeaponDef } from '../../weapons/types';

// Direct imports — no lookupWeapon, no module-order risk
import { GYRO_SKIPPER } from './spinner-d';
import { GYRO_DRILLMINE } from './drillmine';

// ─── Wind Rider flight + sub-projectile on impact ───────────────────────────

interface SpinnerVariant {
    weapon: WeaponDef;
    label: string;
    // Per-variant projectile flags
    ignoreTerrain?: boolean;
    ignoreTanks?: boolean;
    ignoreWind?: boolean;
}

const SPINNER_VARIANTS: SpinnerVariant[] = [
    { weapon: GYRO_SKIPPER,   label: 'Skipper',    ignoreTerrain: false, ignoreTanks: false, ignoreWind: false },
    { weapon: GYRO_DRILLMINE, label: 'Drill Mine', ignoreTerrain: true,  ignoreTanks: true,  ignoreWind: true },
];

function makeWindRider(variantIndex: number): WeaponDef {
    const variant = SPINNER_VARIANTS[variantIndex];
    return registerWeapon({
        id: `gyro_shot${variantIndex + 1}`,
        name: `Koma ${variant.label}`,
        description: `Wind-riding top → ${variant.label} on impact.`,
        tier: 'CHEAP',
        rarity: 'common',
        projectileRadius: KOMA_SPIN.FLIGHT_RADIUS,
        projectileSpeed: KOMA_SPIN.FLIGHT_SPEED,
        color: 0x88CC77,
        gravity: KOMA_SPIN.FLIGHT_GRAVITY,
        bounces: 0,
        bounceEnergyRetention: 0.85,
        damage: KOMA_SPIN.FLIGHT_DAMAGE,
        blastRadius: KOMA_SPIN.FLIGHT_BLAST,
        knockbackMult: KOMA_SPIN.FLIGHT_KNOCKBACK,
        knockbackRadius: 1.0,
        shakeIntensity: 4.0,
        particleScale: 1.0,
        craterShape: SMALL_CRATER,
        noCrater: true,
        windMultiplier: 1.0 + KOMA_SPIN.WIND_AMP,
        projectileShape: 'koma',
        muzzleEffect: 'spawnGyroSpinLaunch',
        edgeguard: { chargeFrames: 20, threshold: 25 },
        onTick(proj, state, fx) {
            const bs = proj.behaviorState;
            bs.flightFrames = (bs.flightFrames || 0) + 1;

            // Wind amplification: 3.5x total (base 1x + WIND_AMP 2.5x)
            proj.vx += state.wind * KOMA_SPIN.WIND_AMP;

            // String unfurl on frame 1
            if (bs.flightFrames === 1) {
                const ox = proj.x - proj.vx * 3;
                const oy = proj.y - proj.vy * 3;
                for (let i = 0; i < KOMA_SPIN.UNFURL_POINTS; i++) {
                    const t = i / (KOMA_SPIN.UNFURL_POINTS - 1);
                    const px = ox + (proj.x - ox) * t;
                    const py = oy + (proj.y - oy) * t - Math.sin(t * Math.PI) * 20;
                    fx.push({ type: 'particles', method: 'spawnExplosion', args: [px, py, 0x88CC77, KOMA_SPIN.UNFURL_PARTICLE_SIZE] });
                }
            }

            // Ribbon trail — ring buffer of recent positions (visual-only, in visualState)
            if (!proj.visualState) proj.visualState = {};
            if (!proj.visualState.ribbon) proj.visualState.ribbon = [];
            const ribbon = proj.visualState.ribbon as RibbonPoint[];
            const speed = Math.sqrt(proj.vx * proj.vx + proj.vy * proj.vy);
            updateKomaRibbon(ribbon, proj.x, proj.y, bs.flightFrames, speed, state.wind);

            // Timeout
            if (bs.flightFrames >= KOMA_SPIN.FLIGHT_TIMEOUT) {
                return 'detonate';
            }

            return null;
        },
        onImpact(proj, x, y, _hitType, _hitIndex, state, fx) {
            const spinner = createProjectile(
                variant.weapon, x, y - 5, 0, 0,
                proj.owner, state.frameCount, 1,
            );
            spinner.ignoreTerrain = variant.ignoreTerrain ?? true;
            spinner.ignoreTanks = variant.ignoreTanks ?? true;
            spinner.ignoreWind = variant.ignoreWind ?? true;
            spinner.behaviorState.lifetime = 0;
            spinner.behaviorState.inheritedVx = proj.vx;

            fx.push({ type: 'particles', method: 'spawnExplosion', args: [x, y, 0x88CC77, 3] });

            return [{
                spawnFrame: state.frameCount + 2,
                spawned: false,
                projectile: spinner,
            }];
        },
    });
}

// Register 2 shots — Skipper (Shot 1) and Drill Mine (Shot 2)
export const GYRO_SHOT1 = makeWindRider(0);
export const GYRO_SHOT2 = makeWindRider(1);
